import React, {forwardRef, useCallback, useMemo} from "react";
import {Box, CloseButton, ColorSwatch, Flex, MultiSelectValueProps, rem, SelectItemProps, Text,} from "@mantine/core";
import {TagDefinitionEntity} from "@ots-frontend/ots-data";
import {BaseMultiSelect, BaseMultiSelectProps} from "../../base-multi-select";

export interface BaseTagDefinitionMultiSelectProps extends Omit<
  BaseMultiSelectProps,
  "data" | "itemComponent" | "valueComponent"
> {
  entities: TagDefinitionEntity[];
  onEntityChange?: (entities: TagDefinitionEntity[]) => void;
}

const getTagColors = (colorHex?: string | null) => {
  if (!colorHex) {
    return {
      backgroundColor: undefined,
      borderColor: undefined,
      textColor: undefined,
    };
  }

  return {
    backgroundColor: `${colorHex}22`,
    borderColor: `${colorHex}55`,
    textColor: colorHex,
  };
}

type TagValueProps = MultiSelectValueProps & {
  value: string;
  colorHex?: string | null;
  tagKey?: string;
};

const TagValue = (props: TagValueProps) => {
  const {
    value,
    label,
    onRemove,
    classNames,
    ...rest
  } = props
  const {textColor} = getTagColors(rest.colorHex);

  return (
    <div {...rest}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          cursor: 'default',
          alignItems: 'center',
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
          border: `${rem(1)} solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[4]
          }`,
          paddingLeft: theme.spacing.xs,
          borderRadius: theme.radius.sm,
        })}
      >
        <Box mr={10}>
          <ColorSwatch h={10} w={10} color={textColor ?? "#FFFFFF"} />
        </Box>
        <Box sx={{ lineHeight: 1, fontSize: rem(12) }}>
          {label}
        </Box>
        <CloseButton
          onMouseDown={onRemove}
          variant="transparent"
          size={22}
          iconSize={14}
          tabIndex={-1}
        />
      </Box>
    </div>
  );
}

type TagItemProps = SelectItemProps & {
  colorHex?: string | null;
  tagKey?: string;
  description?: string | null;
};

const TagItem = forwardRef<
  HTMLDivElement,
  TagItemProps
>((props, ref) => {

  const {
    label,
    value,
    colorHex,
    tagKey,
    description,
    ...rest
  } = props

  const {textColor} = getTagColors(colorHex);

  return (
    <div ref={ref} {...rest}>
      <Flex align="center">
        <Box mr={10}>
          <ColorSwatch h={10} w={10} color={textColor ?? "#FFFFFF"} />
        </Box>
        <div>{label}</div>
        {tagKey ? (
          <Text ml="sm" size="xs" c="dimmed" truncate>
            #{tagKey}
          </Text>
        ) : null}
      </Flex>
    </div>
  )
});

TagItem.displayName = "TagItem";

export function BaseTagDefinitionMultiSelect(props: BaseTagDefinitionMultiSelectProps) {
  const {
    entities,
    onEntityChange,
    onChange,
    label = "Filter by tags",
    placeholder = "Select tags...",
    searchable = true,
    clearable = true,
    nothingFound = "No tags found",
    disabled,
    ...rest
  } = props;

  const data = useMemo(() => {
    return (entities ?? [])
      .filter((tag) => tag.doNotUse === false)
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((tag) => ({
        value: tag.id,
        label: tag.label,
        colorHex: tag.colorHex ?? null,
        tagKey: tag.key,
        description: tag.description ?? null,
      }));
  }, [entities]);

  const _onChange = useCallback(
    (value: string[]) => {
      if (onEntityChange) {
        let selectedEntities: TagDefinitionEntity[] = [];

        if (value) {
          selectedEntities = entities.filter((e) => typeof value.find(v => v === e.id) !== 'undefined');
        }

        onEntityChange(selectedEntities);
      }

      if (onChange) {
        onChange(value);
      }
    },
    [entities, onChange, onEntityChange]
  );

  return (
    <BaseMultiSelect
      {...rest}
      data={data}
      onChange={_onChange}
      searchable={searchable}
      clearable={clearable}
      nothingFound={nothingFound}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      itemComponent={TagItem}
      valueComponent={TagValue}
      maxDropdownHeight={320}
      limit={50}
      filter={(search, selected, item) => {
        if (selected) {
          return false;
        }

        const normalized = search.trim().toLowerCase();

        if (!normalized) {
          return true;
        }

        return (
          item.label.toLowerCase().includes(normalized) ||
          String(item.tagKey ?? "").toLowerCase().includes(normalized) ||
          String(item.description ?? "").toLowerCase().includes(normalized)
        );
      }}
    />
  );
}
